import { faApple } from '@fortawesome/free-brands-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useRef, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { HeadlineM, Text14, Text16 } from 'atoms/typography';
import { Bold } from 'atoms/typography/Bold';
import Dialog from 'components/dialog';
import { GraphQLResult, GraphqlForm, TextField } from 'components/form/Form';
import { TwoFADialog } from 'components/form/TwoFADialog';
import { GoogleReCAPTCHA, ReCAPTCHA } from 'components/recaptcha';
import { DisabledEmailWarning } from 'components/user/DisabledEmailWarning';
import { UpdateUserEmailAttributes } from 'contexts/auth';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { useWalletContext } from 'contexts/wallet';
import useScreenSize from 'hooks/device/useScreenSize';
import { glossary, userAttributes } from 'lib/glossary';
import { GraphQLError } from 'lib/gql';
import { tabletAndAbove } from 'style/mediaQuery';

import { UpdateEmailDialog } from './UpdateEmailDialog';

const messages = defineMessages({
  changeEmail: {
    id: 'Settings.updateEmail.submit',
    defaultMessage: 'Change email',
  },
  privateRelay: {
    id: 'Settings.updateEmail.privateRelay',
    defaultMessage:
      'Apple has provided this anonymized email address (<b>{email}</b>) to protect your privacy. Any emails sent to this address will be automatically forwarded to your real email. If you would like to update your email address, please enter your preferred email using the link below.',
  },
});

const Body = styled(Vertical)`
  padding: var(--triple-unit);
`;
const Container = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    align-items: stretch;
  }
`;
const StyledButtonBase = styled(ButtonBase)`
  color: var(--c-link);
  text-decoration: underline;
`;

export const UpdateEmail = () => {
  const [openDialogEmail, setOpenDialogEmail] = useState<string | null>(null);
  const [openDialogForm, setOpenDialogForm] = useState<boolean>(false);
  const [open2FA, setOpen2FA] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [twoFACallback, setTwoFACallback] = useState<{
    resolve: any;
    reject: any;
  } | null>(null);

  const { up: isTablet } = useScreenSize('tablet');

  const recaptchaRef = useRef<GoogleReCAPTCHA>(null);

  const { formatMessage } = useIntlContext();
  const { currentUser } = useCurrentUserContext();
  const { updateUserEmailWithPassword } = useWalletContext();

  if (!currentUser) return null;

  const { email, otpRequiredForLogin } = currentUser;

  const submitUpdateUserMethodFactory: <T extends { email: string }>(
    callback: (attributes: T) => Promise<{ errors: GraphQLError[] } | null>
  ) => (
    attributes: T,
    doOnResult: (result: GraphQLResult) => void
  ) => Promise<void> =
    <T extends { email: string }>(callback: (attributes: T) => Promise<any>) =>
    async (attributes: T, doOnResult: any) => {
      if (!attributes.email || attributes.email === email) {
        return Promise.reject();
      }

      recaptchaRef.current?.reset();
      const recaptchaTokenV2 = await recaptchaRef.current?.executeAsync();

      if (recaptchaTokenV2) {
        const result = await callback({ ...attributes, recaptchaTokenV2 });

        doOnResult(result);
        if (!result.errors || result.errors.length === 0) {
          setOpenDialogEmail(attributes.email);
          setOpenDialogForm(false);
        }
        return Promise.resolve();
      }
      return Promise.reject();
    };

  const submitUpdateUser = async (
    attributes: UpdateUserEmailAttributes,
    doOnResult: (result: GraphQLResult) => void
  ) => {
    let abort = false;
    const otpAttempt = await new Promise((resolve, reject) => {
      if (!otpRequiredForLogin) return resolve(null);
      setTwoFACallback({ resolve, reject });
      setOpen2FA(true);
      return null;
    }).catch(() => {
      abort = true;
    });

    if (abort) return () => {};
    return submitUpdateUserMethodFactory(updateUserEmailWithPassword)(
      { ...attributes, ...(otpRequiredForLogin ? { otpAttempt } : {}) },
      doOnResult
    );
  };

  return (
    <div>
      <Text16>
        <FormattedMessage {...glossary.accountEmail} />
      </Text16>
      <Text14 color="var(--c-neutral-600)">
        {email.endsWith('@privaterelay.appleid.com') ? (
          <Horizontal>
            <FontAwesomeIcon icon={faApple} /> Apple Private Relay
            <Tooltip
              placement="top"
              title={
                <FormattedMessage
                  {...messages.privateRelay}
                  values={{ email, b: Bold }}
                />
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </Horizontal>
        ) : (
          email
        )}
      </Text14>
      <div>
        <StyledButtonBase onClick={() => setOpenDialogForm(true)}>
          <Text14 bold color="var(--c-link)">
            <FormattedMessage {...messages.changeEmail} />
          </Text14>
        </StyledButtonBase>
      </div>
      <Dialog
        maxWidth="xs"
        fullWidth
        open={openDialogForm}
        onClose={() => setOpenDialogForm(false)}
        title={
          <HeadlineM as="h2" className="text-center">
            <FormattedMessage {...messages.changeEmail} />
          </HeadlineM>
        }
        fullScreen={!isTablet}
      >
        <Body gap={2}>
          <DisabledEmailWarning />
          <Container
            onSubmit={(values, onResult) => {
              submitUpdateUser(
                { ...values, email: values.email.toLowerCase() },
                onResult
              );
            }}
            onChange={values => {
              setHasChanged(email !== values.email);
            }}
            onSuccess={() => setOpenDialogForm(false)}
            render={(Error, SubmitButton) => (
              <Vertical>
                <Error code />
                <TextField
                  name="email"
                  defaultValue={email}
                  label={formatMessage(userAttributes.email)}
                />
                <ReCAPTCHA ref={recaptchaRef} />
                <SubmitButton disabled={!hasChanged} size="large">
                  <FormattedMessage {...messages.changeEmail} />
                </SubmitButton>
              </Vertical>
            )}
          />
        </Body>
      </Dialog>
      {otpRequiredForLogin && (
        <TwoFADialog
          onSubmit={(values, onResult) => {
            twoFACallback?.resolve(values.otpAttempt);
            onResult(values);
          }}
          onSuccess={() => setOpen2FA(false)}
          onClose={() => setOpen2FA(false)}
          onCancel={() => twoFACallback?.reject('no 2FA')}
          open={open2FA}
          reason={formatMessage(messages.changeEmail)}
        />
      )}
      {openDialogEmail && (
        <UpdateEmailDialog
          email={openDialogEmail}
          onClose={() => {
            setOpenDialogEmail(null);
          }}
        />
      )}
    </div>
  );
};
